"use client";

import BudgetFrequencyField from '@/components/crm/BudgetFrequencyField';
import { budgetLabel, budgetSuffix } from '@/utils/budget';
import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { FolderKanban, Building2, Calendar, DollarSign, Users, Flag, Edit, Trash2, Save, Plus, Check, X, FileText } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { get, post, put, del, can, getCurrentOrganization } from "@/utils/service";
import { apiMessage, fieldErrors, money, label, shortDate } from "@/utils/api";
import FloatingDrawer, { DrawerHeader, DetailRow, darkInput, darkLabel, darkPrimary, darkSecondary, darkDanger } from "@/components/crm/FloatingDrawer";
import { useCounts, countText } from "@/components/crm/useCounts";
import ListShell, { toolbarInput, addButton, pill, Avatar } from "@/components/crm/ListShell";
import RecordSelect, { type Picked } from "@/components/crm/RecordSelect";
import { useRemoteList } from "@/components/crm/useRemoteList";
import Kanban from "@/components/crm/Kanban";
import { useStatusBoard } from "@/components/crm/useStatusBoard";
import ViewToggle, { useListView } from "@/components/crm/ViewToggle";
import { toast } from "sonner";
import { useMemberOptions } from "@/components/crm/members";
import AppSelect from "@/components/ui/app-select";
import StatusProgress from "@/components/crm/StatusProgress";
import SearchInput from "@/components/ui/search-input";
import DateInput from "@/components/ui/date-input";

interface Project {
  id: string; name: string; status: string; health: string | null; progress: string | number; start_date: string | null; due_date: string | null;
  contract_id?: string | null; account_id?: string | null; deal_id?: string | null;
  budget_frequency?: string;
  budget: string | null; currency: string; description: string | null; account?: { id: string; name: string } | null; deal?: { id: string; title: string } | null;
  owner?: { name: string; avatar_url?: string | null } | null; can?: { update: boolean; delete: boolean } | null;
}
interface Milestone { id: string; name: string; due_date: string | null; completed_at: string | null }
interface Member { id: string; name: string; email: string; role: string | null }

const PROJECT_JOURNEY = [{ key: "planned", label: "Planned" }, { key: "active", label: "In progress" }, { key: "completed", label: "Completed" }];
const STATUS: Record<string, string> = { planned: "bg-gray-100 text-gray-700", active: "bg-blue-100 text-blue-800", on_hold: "bg-amber-100 text-amber-800", completed: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800" };
const HEALTH: Record<string, string> = { on_track: "text-green-600", at_risk: "text-amber-600", off_track: "text-red-600" };

function ProjectForm({ isOpen, onClose, onSaved, project }: { isOpen: boolean; onClose: () => void; onSaved: () => void; project: Project | null }) {
  const base = getCurrentOrganization()?.base_currency || "UGX";
  const blank = { name: "", status: "planned", health: "on_track", progress: "0", start_date: "", due_date: "", budget: "", budget_frequency: "one_time", currency: base, description: "" };
  const [form, setForm] = useState(blank);
  const [account, setAccount] = useState<Picked>(null);
  const [deal, setDeal] = useState<Picked>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    if (project) {
      setForm({ name: project.name, status: project.status, health: project.health ?? "on_track", progress: String(Number(project.progress)), start_date: project.start_date ?? "", due_date: project.due_date ?? "", budget: project.budget ?? "", budget_frequency: project.budget_frequency ?? "one_time", currency: project.currency, description: project.description ?? "" });
      setAccount(project.account ? { id: project.account.id, label: project.account.name } : null);
      setDeal(project.deal ? { id: project.deal.id, label: project.deal.title } : null);
    } else { setForm(blank); setAccount(null); setDeal(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, project]);
  const set = (k: keyof typeof blank, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (project || !deal?.id) return;
    let live = true;
    get<{ data: { deal: { amount: string; currency: string; budget_frequency?: string; account?: { id: string; name: string } } } }>(`/deals/${deal.id}`).then((response) => {
      if (!live) return;
      const source = response.data.deal;
      setForm((current) => ({ ...current, budget: source.amount, currency: source.currency, budget_frequency: source.budget_frequency ?? 'one_time' }));
      if (source.account) setAccount({ id: source.account.id, label: source.account.name });
    }).catch((failure) => { if (live) notify(apiMessage(failure, 'Could not load the deal budget.')); });
    return () => { live = false; };
  }, [deal?.id, project]);

  const submit = async () => {
    setBusy(true);
    setErrors({});
    const body: Record<string, unknown> = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
    body.account_id = account?.id ?? null;
    body.deal_id = deal?.id ?? null;
    try {
      if (project) await put(`/projects/${project.id}`, body);
      else await post("/projects", body);
      notify(project ? "Project updated" : "Project created");
      onSaved();
      onClose();
    } catch (e) {
      setErrors(fieldErrors(e));
      notify(apiMessage(e, "Could not save the project."));
    } finally { setBusy(false); }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <DrawerHeader title={project ? "Edit Project" : "New Project"} subtitle="Delivery work for an account, usually after a deal is won" />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div><label className={darkLabel}>Name *</label><input className={darkInput} value={form.name} onChange={(e) => set("name", e.target.value)} />{errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}</div>
        <div><label className={darkLabel}>Account</label><RecordSelect kind="accounts" value={account} onChange={setAccount} /></div>
        <div><label className={darkLabel}>From deal</label><RecordSelect kind="deals" value={deal} onChange={setDeal} params={account ? { account_id: account.id } : undefined} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={darkLabel}>Status</label><AppSelect className={darkInput} value={form.status} onChange={(e) => set("status", e.target.value)}>{Object.keys(STATUS).map((s) => <option key={s} value={s}>{label(s)}</option>)}</AppSelect></div>
          <div><label className={darkLabel}>Health</label><AppSelect className={darkInput} value={form.health} onChange={(e) => set("health", e.target.value)}><option value="on_track">On track</option><option value="at_risk">At risk</option><option value="off_track">Off track</option></AppSelect></div>
        </div>
        <BudgetFrequencyField value={form.budget_frequency} onChange={(value) => set('budget_frequency', value)} className={darkInput} labelClassName={darkLabel} />
        <div><label className={darkLabel}>Progress ({form.progress}%)</label><input type="range" min="0" max="100" className="w-full accent-primary" value={form.progress} onChange={(e) => set("progress", e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={darkLabel}>Start</label><DateInput className={darkInput} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
          <div><label className={darkLabel}>Due</label><DateInput className={darkInput} value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />{errors.due_date && <p className="mt-1 text-xs text-red-400">{errors.due_date}</p>}</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={darkLabel}>{budgetLabel(form.budget_frequency)}</label><input type="number" min="0" step="0.01" className={darkInput} value={form.budget} onChange={(e) => set("budget", e.target.value)} /></div>
          <div><label className={darkLabel}>Currency</label><AppSelect className={darkInput} value={form.currency} onChange={(e) => set("currency", e.target.value)}>{[...new Set([form.currency, base, "UGX", "USD", "EUR", "GBP"])].map((c) => <option key={c}>{c}</option>)}</AppSelect></div>
        </div>
        <div><label className={darkLabel}>Description</label><textarea rows={3} className={`${darkInput} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
      </div>
      <div className="p-6 border-t border-gray-800 space-y-3">
        <button onClick={submit} disabled={busy || !form.name} className={darkPrimary}><Save className="w-4 h-4" />{busy ? "Saving..." : project ? "Update Project" : "Create Project"}</button>
        <button onClick={onClose} className={darkSecondary}>Cancel</button>
      </div>
    </FloatingDrawer>
  );
}

function ProjectDrawer({ project, onClose, onEdit, onDeleted }: { project: Project | null; onClose: () => void; onEdit: (p: Project) => void; onDeleted: () => void }) {
  const memberOptions = useMemberOptions();
  const [members, setMembers] = useState<Member[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [addMember, setAddMember] = useState("");
  const [newMilestone, setNewMilestone] = useState({ name: "", due_date: "" });
  const editable = project?.can?.update !== false && can("projects.update");

  const load = useCallback(async () => {
    if (!project) return;
    const [m, ms] = await Promise.all([
      get<{ data: { members: Member[] } }>(`/projects/${project.id}/members`).catch(() => ({ data: { members: [] } })),
      get<{ data: { milestones: Milestone[] } }>(`/projects/${project.id}/milestones`).catch(() => ({ data: { milestones: [] } })),
    ]);
    setMembers(m.data.members);
    setMilestones(ms.data.milestones);
  }, [project]);
  useEffect(() => { load(); }, [load]);

  if (!project) return <FloatingDrawer isOpen={false} onClose={onClose}>{null}</FloatingDrawer>;

  const saveMembers = async (next: Member[]) => {
    try {
      await put(`/projects/${project.id}/members`, { members: next.map((m) => ({ id: m.id, role: m.role })) });
      load();
    } catch (e) { notify(apiMessage(e, "Could not update the project team.")); }
  };
  const addMilestone = async () => {
    try {
      await post(`/projects/${project.id}/milestones`, { name: newMilestone.name, due_date: newMilestone.due_date || null });
      setNewMilestone({ name: "", due_date: "" });
      load();
    } catch (e) { notify(apiMessage(e, "Could not add the milestone.")); }
  };
  const toggleMilestone = async (m: Milestone) => {
    try { await put(`/projects/${project.id}/milestones/${m.id}`, { completed: !m.completed_at }); load(); }
    catch (e) { notify(apiMessage(e, "Could not update the milestone.")); }
  };
  const removeMilestone = async (m: Milestone) => {
    try { await del(`/projects/${project.id}/milestones/${m.id}`); load(); }
    catch (e) { notify(apiMessage(e, "Could not remove the milestone.")); }
  };
  const remove = async () => {
    if (!await confirmDialog(`Delete ${project.name}?`)) return;
    try { await del(`/projects/${project.id}`); notify("Project deleted"); onDeleted(); }
    catch (e) { notify(apiMessage(e, "Could not delete the project.")); }
  };

  return (
    <FloatingDrawer isOpen onClose={onClose}>
      <DrawerHeader title="Project" subtitle={project.name} />
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        <StatusProgress dark steps={PROJECT_JOURNEY}
          current={["on_hold", "cancelled"].includes(project.status) ? "active" : project.status}
          exit={project.status === "cancelled" ? { label: "Cancelled" } : project.status === "on_hold" ? { label: "On hold" } : null} />
        <div className="flex items-center gap-2">
          <span className={pill(STATUS[project.status] ?? STATUS.planned)}>{label(project.status)}</span>
          {project.health && <span className={`text-xs ${HEALTH[project.health] ?? "text-gray-400"}`}>● {label(project.health)}</span>}
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold text-white">Progress</p>
          <div className="flex items-center gap-3"><div className="h-2 flex-1 rounded-full bg-gray-800"><div className="h-2 rounded-full bg-primary" style={{ width: `${Number(project.progress)}%` }} /></div><span className="text-xs">{Number(project.progress)}%</span></div>
        </div>
        <DetailRow icon={Building2} label="Account">{project.account?.name ?? "—"}{project.deal ? ` · from deal ${project.deal.title}` : ""}</DetailRow>
        <DetailRow icon={Calendar} label="Timeline">{shortDate(project.start_date)} → {shortDate(project.due_date)}</DetailRow>
        <DetailRow icon={DollarSign} label="Budget">{money(project.budget, project.currency)}{budgetSuffix(project.budget_frequency)}</DetailRow>
        {project.description && <DetailRow icon={FileText} label="Description"><span className="whitespace-pre-line">{project.description}</span></DetailRow>}

        <div>
          <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-white"><Users className="w-4 h-4" />Team ({members.length})</p>
          <div className="space-y-1.5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-lg bg-gray-900 px-3 py-2 text-xs">
                <span>{m.name}{m.role && <span className="text-gray-400"> · {m.role}</span>}</span>
                {editable && <button aria-label={`Remove ${m.name}`} onClick={() => saveMembers(members.filter((x) => x.id !== m.id))} className="text-gray-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
          </div>
          {editable && (
            <div className="mt-2 flex gap-2">
              <AppSelect className={darkInput} value={addMember} onChange={(e) => setAddMember(e.target.value)}>
                <option value="">Add a team member…</option>
                {memberOptions.filter((o) => !members.some((m) => m.id === o.id)).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </AppSelect>
              <button disabled={!addMember} onClick={() => { const o = memberOptions.find((x) => x.id === addMember); if (o) saveMembers([...members, { id: o.id, name: o.name, email: o.email, role: null }]); setAddMember(""); }} className="rounded-lg bg-gray-800 px-3 text-white disabled:opacity-50"><Plus className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>

        <div>
          <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-white"><Flag className="w-4 h-4" />Milestones</p>
          <div className="space-y-1.5">
            {milestones.length === 0 && <p className="text-xs text-gray-500">No milestones yet.</p>}
            {milestones.map((m) => (
              <div key={m.id} className="flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-xs">
                <button disabled={!editable} onClick={() => toggleMilestone(m)} aria-label={m.completed_at ? "Mark as not done" : "Mark as done"} className={`flex h-4 w-4 items-center justify-center rounded border ${m.completed_at ? "border-primary bg-primary" : "border-gray-600"}`}>{m.completed_at && <Check className="h-3 w-3" />}</button>
                <span className={`flex-1 ${m.completed_at ? "text-gray-500 line-through" : ""}`}>{m.name}</span>
                <span className="text-gray-400">{shortDate(m.due_date)}</span>
                {editable && <button aria-label="Remove milestone" onClick={() => removeMilestone(m)} className="text-gray-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
          </div>
          {editable && (
            <div className="mt-2 grid grid-cols-[1fr_auto_auto] gap-2">
              <input className={darkInput} placeholder="Milestone" value={newMilestone.name} onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })} />
              <DateInput className={darkInput} value={newMilestone.due_date} onChange={(e) => setNewMilestone({ ...newMilestone, due_date: e.target.value })} />
              <button disabled={!newMilestone.name} onClick={addMilestone} className="rounded-lg bg-gray-800 px-3 text-white disabled:opacity-50"><Plus className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
      </div>
      <div className="p-3 border-t border-gray-800 grid grid-cols-2 gap-2">
        {project.contract_id ? <Link className={darkSecondary} to={`/contracts/${project.contract_id}`}>View contract</Link> : editable && project.account && can('contracts.create') && <Link className={darkSecondary} to={`/contracts/new?project_id=${project.id}`}>Create contract</Link>}
        <button disabled={!editable} onClick={() => onEdit(project)} className={darkPrimary}><Edit className="w-4 h-4" />Edit</button>
        <button disabled={project.can?.delete === false} onClick={remove} className={darkDanger}><Trash2 className="w-4 h-4" />Delete</button>
      </div>
    </FloatingDrawer>
  );
}

const BOARD_STATUSES = [
  { id: "planned", title: "Planned", dot: "bg-gray-400" },
  { id: "active", title: "Active", dot: "bg-blue-500" },
  { id: "on_hold", title: "On hold", dot: "bg-amber-400" },
  { id: "completed", title: "Completed", dot: "bg-green-500" },
  { id: "cancelled", title: "Cancelled", dot: "bg-red-400" },
];

export default function DeliveryProjectsPage() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("due_date");
  const list = useRemoteList<Project>("/projects", { search: query, status, sort });
  const { counts, loading: countsLoading } = useCounts("/projects", { active: { status: "active" }, planned: { status: "planned" }, hold: { status: "on_hold" }, done: { status: "completed" } }, list.rows.length);
  const [view, setView] = useListView("projects", ["board", "cards"] as const, "board");
  const [boardKey, setBoardKey] = useState(0);
  const board = useStatusBoard<Project>("/projects-board", BOARD_STATUSES, { search: query || undefined, sort: "due_date" }, boardKey, view === "board");
  const reloadAll = () => { list.reload(); setBoardKey((k) => k + 1); };
  const moveProject = async (p: Project, _from: string, to: string) => {
    try {
      await put(`/projects/${p.id}`, { status: to });
      toast.success(`${p.name} moved to ${label(to)}`);
      list.reload();
    } catch (e) {
      notify(apiMessage(e, "Could not move the project."));
      return false;
    }
  };
  const [selected, setSelected] = useState<Project | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const card = (p: Project, onBoard = false) => {
    const progress = Math.max(0, Math.min(100, Number(p.progress) || 0));
    const overdue = !!p.due_date && !["completed", "cancelled"].includes(p.status) && new Date(`${p.due_date}T23:59:59`) < new Date();
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">{p.name}</p>
            <p className="truncate text-xs text-gray-500">{p.account?.name ?? "Internal project"}</p>
          </div>
          {!onBoard && <span className={pill(STATUS[p.status] ?? STATUS.planned)}>{label(p.status)}</span>}
        </div>
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-gray-500">Progress</span>
            <span className="font-medium text-gray-900">{progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${progress}%` }} /></div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
          <span className={`inline-flex items-center gap-1 ${overdue ? "font-semibold text-red-600" : ""}`}><Calendar className="h-3.5 w-3.5" />{p.due_date ? `${overdue ? "Overdue · " : "Due "}${shortDate(p.due_date)}` : "No due date"}</span>
          {p.health && <span className={`inline-flex items-center gap-1 ${HEALTH[p.health] ?? "text-gray-500"}`}><Flag className="h-3.5 w-3.5" />{label(p.health)}</span>}
        </div>
        <div className="min-h-[1rem] flex-1" />
        <div className="flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
          <span>{p.budget ? `${money(p.budget, p.currency)}${budgetSuffix(p.budget_frequency)}` : "No budget set"}</span>
          {p.owner && <span className="flex items-center gap-2">{p.owner.name}<Avatar name={p.owner.name} src={p.owner.avatar_url} className="h-6 w-6 text-[10px]" /></span>}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Projects" description="Delivery work, teams and milestones">
          <ViewToggle view={view} onChange={setView} options={[{ id: "board", label: "Board" }, { id: "cards", label: "Grid" }]} />
          {view === "board" && can("projects.create") && <button onClick={() => { setEditing(null); setFormOpen(true); }} className={addButton}>New Project</button>}
        </PageHeader>
        {view === "board" ? (
          <div className="space-y-4">
            <SearchInput className="w-full sm:w-72" placeholder="Search projects…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Kanban
              columns={board.columns}
              loading={board.loading}
              noun="Project"
              emptyText="No projects"
              getId={(p) => p.id}
              onOpen={setSelected}
              canMove={(p) => p.can?.update !== false}
              onMove={moveProject}
              renderCard={(p) => <div className="pr-6">{card(p, true)}</div>}
            />
          </div>
        ) : (
        <ListShell
          renderCard={(p) => card(p)}
          rowKey={(r) => r.id}
          rows={list.rows}
          loading={list.loading}
          pagination={list.pagination}
          onPage={list.setPage}
          onPerPage={list.setPerPage}
          noun="projects"
          onRowClick={setSelected}
          grid={{
            id: "projects",
            search: <SearchInput className="w-full sm:w-64" placeholder="Search projects…" value={search} onChange={(e) => setSearch(e.target.value)} />,
            filters: (
              <label className="text-xs text-gray-600">Status
                <AppSelect className={`${toolbarInput} mt-1`} value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option>{Object.keys(STATUS).map((s) => <option key={s} value={s}>{label(s)}</option>)}</AppSelect>
              </label>
            ),
            activeFilters: status ? 1 : 0,
            onClearFilters: () => setStatus(""),
            sortOptions: [{ value: "due_date", label: "Due soonest" }, { value: "-progress", label: "Most progress" }, { value: "progress", label: "Least progress" }, { value: "-budget", label: "Largest budget" }, { value: "name", label: "Name" }, { value: "-created_at", label: "Newest" }],
            sort, onSort: setSort,
            stats: [
              { label: "Active", value: countText(counts.active), tone: "good" },
              { label: "Planned", value: countText(counts.planned) },
              { label: "On hold", value: countText(counts.hold), tone: counts.hold ? "warn" : "default" },
              { label: "Completed", value: countText(counts.done) },
            ],
            statsLoading: countsLoading,
            exportEntity: "projects", exportPermission: "projects.export",
          }}
          onAdd={can("projects.create") ? () => { setEditing(null); setFormOpen(true); } : undefined}
          addLabel="New Project"
          empty={{ icon: FolderKanban, title: "No projects", text: "Create a project to plan delivery for an account, with a team and milestones.", action: "Create First Project" }}
        />
        )}
      </div>
      <ProjectDrawer project={selected} onClose={() => setSelected(null)} onEdit={(p) => { setSelected(null); setEditing(p); setFormOpen(true); }} onDeleted={() => { setSelected(null); reloadAll(); }} />
      <ProjectForm isOpen={formOpen} onClose={() => setFormOpen(false)} onSaved={reloadAll} project={editing} />
    </div>
  );
}

